import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {ArrowIcon} from '@/components/common/icons/ArrowIcon'
import {GuideSelectionContentBox} from '@/components/guide/GuideSelectionContentBox'
import {getClassAssistContent} from '@/content/classAssist'
import {Link, useTranslation} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {getClassLabel} from '@/utils/class-assist/helpers'
import {getGSClassNamesByLanguage} from '@/utils/goods-and-services/classNames'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

type ClassAssistRecommendedClassesProps = {
  sectorName: string
  classes: {
    items: {name: string; slug: string}[]
    classCode: string
  }[]
}

export const ClassAssistRecommendedClasses: FC<ClassAssistRecommendedClassesProps> = ({
  classes,
  sectorName,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {includesText, learnMoreAboutClassText, recommendedClassesTitle} =
    getClassAssistContent(language)

  const classNames = getGSClassNamesByLanguage(language)

  return (
    <>
      <GuideSelectionContentBox title={t(recommendedClassesTitle, {name: sectorName})}>
        {classes.map(({classCode, items}) => (
          <div key={classCode} className="class">
            <Text variant={TextVariant.h6} element="h4" fontWeight={TextFontWeight.bold}>
              {getClassLabel(t, classNames, classCode)}
            </Text>
            <div className="note">
              <Text>{t(includesText)}</Text>
            </div>
            <ul className="list">
              {items.map(({name, slug}) => (
                <li key={name} className="item">
                  <Text>
                    <Link
                      href={routeTo.classAssist.class(classCode, slug)}
                      prefetch={false}
                      legacyBehavior
                    >
                      <a className="underline">{name}</a>
                    </Link>
                  </Text>
                </li>
              ))}
            </ul>
            <div className="button-wrapper">
              <Link href={routeTo.classAssist.class(classCode)} prefetch={false}>
                <Button
                  type="button"
                  icon={<ArrowIcon size={IconSize.small} />}
                  size={ButtonSize.small}
                  isHoverable
                >
                  {t(learnMoreAboutClassText, {classCode})}
                </Button>
              </Link>
            </div>
          </div>
        ))}
      </GuideSelectionContentBox>

      <style jsx>{`
        .class + .class {
          margin-top: ${theme.spacing[5]};
          padding-top: ${theme.spacing[5]};
          border-top: 1px solid ${theme.palette.gray[200]};
        }
        .note {
          margin-top: ${theme.spacing[2]};
        }
        .list {
          margin-top: ${theme.spacing[4]};
        }
        .item {
          list-style-type: disc;
          margin-left: ${theme.spacing[4]};
        }
        .item + .item {
          margin-top: ${theme.spacing[2]};
        }
        .button-wrapper {
          margin-top: ${theme.spacing[4]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .note {
            margin-top: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
